import React, { useState, useRef, Fragment, Component } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { FloatingOrbs } from './FloatingOrbs';
import { TrendingUp, Clock, Shield, Users, Zap, Target, BarChart3, ArrowUpRight, CheckCircle, Gauge, Briefcase, Building2, Sparkles, Activity, DollarSign, UserCheck, Workflow, Brain, LineChart, FileCheck, Lock, Layers } from 'lucide-react';
// Icon mapping for dynamic icon rendering
const iconMap: Record<string, any> = {
  TrendingUp,
  Clock,
  Shield,
  Users,
  Zap,
  Target,
  BarChart3,
  ArrowUpRight,
  CheckCircle,
  Gauge,
  Briefcase,
  Building2,
  Sparkles,
  Activity,
  DollarSign,
  UserCheck,
  Workflow,
  Brain,
  LineChart,
  FileCheck,
  Lock,
  Layers
};
// Expertise data for each service
const serviceExpertise: Record<string, {
  title: string;
  description: string;
  capabilities: string[];
  caseStudy: {
    industry: string;
    challenge: string;
    solution: string;
  };
  outcomes: Array<{
    icon: string;
    label: string;
  }>;
}> = {
  Agentforce: {
    title: 'Agentforce',
    description: 'We run strategic workshops to map your customer service and sales operations, identifying high-volume, repetitive processes where autonomous AI agents can deliver immediate impact. Our approach designs agent orchestration that fits seamlessly into your existing workflows.',
    capabilities: ['Autonomous agent design and orchestration', 'Natural language processing and intent recognition', 'Multi-channel agent deployment', 'Agent performance monitoring and optimization'],
    caseStudy: {
      industry: 'Financial Services Firm',
      challenge: 'Support team overwhelmed with routine account inquiries, leading to long wait times and inconsistent responses across channels.',
      solution: 'Deployed autonomous agents to handle tier-1 inquiries across email, chat, and phone, with intelligent escalation to human agents for complex cases. Agents now resolve 65% of inquiries without human intervention.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '40% reduction in manual support tasks'
    }, {
      icon: 'Zap',
      label: 'Real-time intelligent decision-making'
    }, {
      icon: 'Users',
      label: 'Seamless human-agent handoffs'
    }, {
      icon: 'Clock',
      label: '24/7 automated customer support'
    }]
  },
  Flow: {
    title: 'Flow Automation',
    description: 'Through collaborative workshops, we analyze your manual processes and design sophisticated Flow automations that orchestrate complex business logic across your Salesforce ecosystem. Each workflow is architected for reliability, scalability, and zero-error execution.',
    capabilities: ['Complex multi-step process automation', 'Cross-object workflow orchestration', 'Screen flows for guided user experiences', 'Integration with external systems'],
    caseStudy: {
      industry: 'Healthcare Provider Network',
      challenge: 'Patient referral process required 12+ manual steps across multiple systems, causing delays and data entry errors.',
      solution: 'Built end-to-end Flow automation connecting patient intake, provider matching, appointment scheduling, and insurance verification. Reduced referral processing time from 3 days to 2 hours.'
    },
    outcomes: [{
      icon: 'Gauge',
      label: '60% faster process completion'
    }, {
      icon: 'CheckCircle',
      label: 'Zero-error data processing'
    }, {
      icon: 'Shield',
      label: 'Consistent business rule enforcement'
    }, {
      icon: 'Workflow',
      label: 'Automated cross-system orchestration'
    }]
  },
  'Omni-Channel': {
    title: 'Omni-Channel',
    description: 'We assess your current customer engagement channels and team capacity, then design intelligent routing strategies that balance workloads and ensure the right work reaches the right person at the right time across all touchpoints.',
    capabilities: ['Intelligent work routing and assignment', 'Multi-channel presence management', 'Capacity-based workload distribution', 'Real-time queue monitoring'],
    caseStudy: {
      industry: 'National Retail Chain',
      challenge: 'Customer inquiries across phone, email, chat, and social media created uneven workloads and inconsistent response times.',
      solution: 'Implemented Omni-Channel routing with skills-based assignment and real-time capacity monitoring. Work now flows intelligently to available agents based on expertise and current workload.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '35% improvement in response times'
    }, {
      icon: 'Users',
      label: 'Balanced team workloads'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced customer satisfaction'
    }, {
      icon: 'Activity',
      label: 'Real-time capacity visibility'
    }]
  },
  'Salesforce Platform': {
    title: 'Salesforce Platform',
    description: 'Our workshops dive deep into your business model to architect the foundational platform that will power your operations for years to come. We design scalable data models, security frameworks, and governance structures that support growth.',
    capabilities: ['Custom object and field architecture', 'Security model design and implementation', 'Platform governance and best practices', 'Scalable data architecture'],
    caseStudy: {
      industry: 'Technology SaaS Company',
      challenge: 'Rapid growth outpaced their initial Salesforce setup, causing data inconsistencies and security gaps across business units.',
      solution: 'Redesigned platform architecture with proper object relationships, role hierarchies, and sharing rules. Established governance framework for sustainable scaling.'
    },
    outcomes: [{
      icon: 'Shield',
      label: 'Enterprise-grade security and compliance'
    }, {
      icon: 'Layers',
      label: 'Scalable system architecture'
    }, {
      icon: 'Target',
      label: 'Future-proof platform foundation'
    }, {
      icon: 'Lock',
      label: 'Robust data governance'
    }]
  },
  'Data Cloud': {
    title: 'Data Cloud',
    description: 'We map your fragmented customer data sources and design a unified Data Cloud strategy that creates a real-time, 360-degree customer view. Our approach harmonizes data from every touchpoint into actionable intelligence.',
    capabilities: ['Multi-source data integration', 'Real-time data harmonization', 'Customer 360 profile creation', 'Data activation across channels'],
    caseStudy: {
      industry: 'Insurance Provider',
      challenge: 'Customer data scattered across policy systems, claims platforms, and marketing tools prevented personalized service and created compliance risks.',
      solution: 'Unified 15+ data sources into Data Cloud with real-time synchronization. Created golden customer records that power personalized experiences across all channels.'
    },
    outcomes: [{
      icon: 'Target',
      label: 'Single source of truth for customer data'
    }, {
      icon: 'Zap',
      label: 'Real-time personalization'
    }, {
      icon: 'CheckCircle',
      label: 'Cross-channel data consistency'
    }, {
      icon: 'Brain',
      label: 'AI-ready unified data model'
    }]
  },
  'Sales Cloud': {
    title: 'Sales Cloud',
    description: 'Through discovery workshops with your sales leadership, we design Sales Cloud implementations that accelerate pipeline velocity, eliminate friction, and provide the intelligence your team needs to close deals faster.',
    capabilities: ['Sales process optimization', 'Pipeline and forecast management', 'Territory and quota planning', 'Sales intelligence and analytics'],
    caseStudy: {
      industry: 'Manufacturing Company',
      challenge: 'Sales team spending 40% of time on administrative tasks instead of selling. Forecast accuracy below 60%.',
      solution: 'Streamlined sales processes with automation, built intelligent forecasting models, and implemented territory management. Freed up 15 hours per rep per week.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '25% increase in sales productivity'
    }, {
      icon: 'Target',
      label: 'Improved forecast accuracy'
    }, {
      icon: 'Clock',
      label: 'Faster deal cycles'
    }, {
      icon: 'BarChart3',
      label: 'Data-driven pipeline insights'
    }]
  },
  'Service Cloud': {
    title: 'Service Cloud',
    description: 'We analyze your support operations to design Service Cloud solutions that scale exceptional customer experiences. Our workshops identify bottlenecks and opportunities for automation, self-service, and agent productivity.',
    capabilities: ['Case management optimization', 'Knowledge base design', 'Self-service portal development', 'Service analytics and reporting'],
    caseStudy: {
      industry: 'Software Company',
      challenge: 'Support team drowning in repetitive tickets. Average resolution time of 48 hours. Customer satisfaction declining.',
      solution: 'Built comprehensive knowledge base with AI-powered article recommendations, implemented case deflection flows, and optimized routing. Self-service now resolves 40% of inquiries.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '50% reduction in case resolution time'
    }, {
      icon: 'CheckCircle',
      label: 'Increased first-contact resolution'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced customer satisfaction scores'
    }, {
      icon: 'Users',
      label: 'Improved agent productivity'
    }]
  },
  'Marketing Cloud': {
    title: 'Marketing Cloud',
    description: 'Our strategic sessions map your customer journeys and design Marketing Cloud implementations that deliver personalized, data-driven campaigns across every channel. We build automation that nurtures relationships at scale.',
    capabilities: ['Journey orchestration and automation', 'Multi-channel campaign management', 'Audience segmentation and targeting', 'Marketing analytics and attribution'],
    caseStudy: {
      industry: 'E-commerce Retailer',
      challenge: 'Generic email blasts with 2% engagement. No visibility into customer journey effectiveness or campaign ROI.',
      solution: 'Designed personalized journey orchestration based on behavior, purchase history, and preferences. Built attribution model to track campaign performance.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '3x improvement in engagement rates'
    }, {
      icon: 'Target',
      label: 'Personalized customer journeys'
    }, {
      icon: 'BarChart3',
      label: 'Data-driven campaign optimization'
    }, {
      icon: 'DollarSign',
      label: 'Clear marketing ROI visibility'
    }]
  },
  'Experience Cloud': {
    title: 'Experience Cloud',
    description: 'We collaborate with your teams to design branded digital experiences that connect customers, partners, and employees. Our approach creates self-service portals that reduce support costs while enhancing engagement.',
    capabilities: ['Custom portal and community design', 'Self-service experience optimization', 'Partner relationship management', 'Mobile-responsive experience delivery'],
    caseStudy: {
      industry: 'B2B Distribution Company',
      challenge: 'Partners calling for order status, pricing, and inventory information. Support team handling 200+ calls daily.',
      solution: 'Built partner portal with real-time order tracking, pricing catalogs, and inventory visibility. Partners now self-serve 80% of inquiries.'
    },
    outcomes: [{
      icon: 'DollarSign',
      label: 'Reduced support costs through self-service'
    }, {
      icon: 'Users',
      label: 'Enhanced partner collaboration'
    }, {
      icon: 'Sparkles',
      label: 'Branded, on-brand digital experiences'
    }, {
      icon: 'CheckCircle',
      label: '80% inquiry deflection rate'
    }]
  },
  'Revenue Cloud': {
    title: 'Revenue Cloud',
    description: 'Through workshops with your revenue operations team, we design Revenue Cloud solutions that streamline quote-to-cash processes, eliminate pricing errors, and automate complex billing scenarios for subscription and usage-based models.',
    capabilities: ['CPQ configuration and optimization', 'Subscription and usage-based billing', 'Revenue recognition automation', 'Contract lifecycle management'],
    caseStudy: {
      industry: 'SaaS Platform Provider',
      challenge: 'Complex pricing models causing quote errors. Manual contract amendments taking days. Revenue recognition spreadsheets breaking.',
      solution: 'Implemented Revenue Cloud with automated pricing rules, self-service amendments, and revenue recognition automation. Quotes now generated in minutes with zero errors.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '40% faster quote generation'
    }, {
      icon: 'BarChart3',
      label: 'Accurate revenue forecasting'
    }, {
      icon: 'Workflow',
      label: 'Automated billing and invoicing'
    }, {
      icon: 'CheckCircle',
      label: 'Eliminated pricing errors'
    }]
  },
  'Field Service': {
    title: 'Field Service',
    description: 'We assess your field operations to design intelligent scheduling, mobile workforce solutions, and predictive maintenance strategies that optimize technician utilization and improve first-time fix rates.',
    capabilities: ['Intelligent work order scheduling', 'Mobile technician enablement', 'Asset and inventory management', 'Predictive maintenance'],
    caseStudy: {
      industry: 'Utilities Company',
      challenge: 'Inefficient scheduling causing technician downtime. First-time fix rate below 60%. Customer appointment windows too wide.',
      solution: 'Deployed Field Service with AI-powered scheduling, mobile app for technicians, and parts inventory management. Optimized routes and skills-based assignment.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: '30% increase in first-time fix rates'
    }, {
      icon: 'Gauge',
      label: 'Optimized technician utilization'
    }, {
      icon: 'DollarSign',
      label: 'Reduced operational costs'
    }, {
      icon: 'Clock',
      label: 'Tighter appointment windows'
    }]
  },
  CPQ: {
    title: 'CPQ (Configure, Price, Quote)',
    description: 'Our discovery process maps your product catalog and pricing complexity to design CPQ solutions that guide sales reps through configuration, apply accurate pricing rules, and generate quotes in minutes instead of days.',
    capabilities: ['Product configuration rules', 'Dynamic pricing and discounting', 'Guided selling workflows', 'Approval process automation'],
    caseStudy: {
      industry: 'Manufacturing Equipment Supplier',
      challenge: 'Complex product configurations causing quote errors. Sales reps spending hours building quotes. Approval bottlenecks delaying deals.',
      solution: 'Built CPQ with guided configuration, automated pricing rules, and streamlined approvals. Integrated with ERP for real-time inventory and cost data.'
    },
    outcomes: [{
      icon: 'Clock',
      label: '50% faster quote generation'
    }, {
      icon: 'CheckCircle',
      label: 'Reduced pricing errors'
    }, {
      icon: 'DollarSign',
      label: 'Improved deal profitability'
    }, {
      icon: 'Workflow',
      label: 'Automated approval workflows'
    }]
  },
  MuleSoft: {
    title: 'MuleSoft Integration',
    description: 'We map your technology landscape and design API-led integration architecture that connects Salesforce with your entire ecosystem. Our approach creates reusable integration assets that accelerate future connectivity.',
    capabilities: ['API design and development', 'System integration architecture', 'Real-time data synchronization', 'Integration governance'],
    caseStudy: {
      industry: 'Financial Services Institution',
      challenge: 'Salesforce disconnected from core banking systems. Manual data entry causing errors. No real-time visibility into customer accounts.',
      solution: 'Built MuleSoft integration layer connecting Salesforce to 8 backend systems. Created reusable APIs for account data, transactions, and customer profiles.'
    },
    outcomes: [{
      icon: 'Layers',
      label: 'Seamless system connectivity'
    }, {
      icon: 'Zap',
      label: 'Real-time data availability'
    }, {
      icon: 'Target',
      label: 'Reduced integration complexity'
    }, {
      icon: 'CheckCircle',
      label: 'Reusable API assets'
    }]
  },
  Tableau: {
    title: 'Tableau Analytics',
    description: 'Through analytics workshops, we identify your key business questions and design Tableau dashboards that transform Salesforce data into actionable insights. Our visualizations empower data-driven decision-making across your organization.',
    capabilities: ['Interactive dashboard design', 'Advanced analytics and forecasting', 'Self-service analytics enablement', 'Embedded analytics integration'],
    caseStudy: {
      industry: 'Healthcare Network',
      challenge: 'Executives relying on static reports delivered weekly. No visibility into real-time performance. Data analysts spending days building custom reports.',
      solution: 'Built executive dashboards with real-time Salesforce data, predictive analytics for patient volume, and self-service reporting for department heads.'
    },
    outcomes: [{
      icon: 'Brain',
      label: 'Data-driven decision making'
    }, {
      icon: 'Zap',
      label: 'Real-time business insights'
    }, {
      icon: 'Users',
      label: 'Democratized analytics access'
    }, {
      icon: 'LineChart',
      label: 'Predictive forecasting'
    }]
  },
  'Einstein AI': {
    title: 'Einstein AI',
    description: 'We run AI readiness workshops to identify high-value use cases for predictive and generative AI within your Salesforce processes. Our implementations embed intelligence directly into workflows for automated decision-making.',
    capabilities: ['Predictive lead and opportunity scoring', 'AI-powered recommendations', 'Natural language processing', 'Custom AI model development'],
    caseStudy: {
      industry: 'B2B Technology Company',
      challenge: 'Sales team wasting time on low-quality leads. No visibility into which opportunities would close. Manual prioritization causing missed revenue.',
      solution: 'Implemented Einstein Lead Scoring and Opportunity Insights. AI now predicts close probability and recommends next best actions for each deal.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Intelligent automation at scale'
    }, {
      icon: 'Brain',
      label: 'Predictive insights for better decisions'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced user productivity'
    }, {
      icon: 'Target',
      label: 'AI-driven prioritization'
    }]
  },
  Identity: {
    title: 'Identity & Access Management',
    description: 'Our security workshops assess your current access controls and design enterprise-grade identity solutions that balance security with user experience. We implement SSO, MFA, and role-based access that scales with your organization.',
    capabilities: ['Single sign-on (SSO) implementation', 'Multi-factor authentication', 'Role-based access control', 'Identity governance'],
    caseStudy: {
      industry: 'Professional Services Firm',
      challenge: 'Multiple login credentials causing password fatigue. No MFA creating security risks. Manual user provisioning taking days.',
      solution: 'Implemented SSO with existing identity provider, enforced MFA for all users, and automated user provisioning based on role changes.'
    },
    outcomes: [{
      icon: 'Shield',
      label: 'Enhanced security posture'
    }, {
      icon: 'Users',
      label: 'Streamlined user access'
    }, {
      icon: 'Lock',
      label: 'Compliance with security standards'
    }, {
      icon: 'Workflow',
      label: 'Automated provisioning'
    }]
  },
  'Financial Services Cloud': {
    title: 'Financial Services Cloud',
    description: 'We partner with wealth management, banking, and insurance firms to design industry-specific solutions that deliver holistic client views, ensure regulatory compliance, and enhance advisor productivity.',
    capabilities: ['Client relationship management', 'Financial account aggregation', 'Compliance and regulatory reporting', 'Advisor productivity tools'],
    caseStudy: {
      industry: 'Wealth Management Firm',
      challenge: 'Advisors juggling multiple systems to view client portfolios. No unified household view. Compliance reporting manual and error-prone.',
      solution: 'Deployed Financial Services Cloud with household relationship mapping, integrated account data from custodians, and automated compliance workflows.'
    },
    outcomes: [{
      icon: 'Users',
      label: 'Holistic client view'
    }, {
      icon: 'FileCheck',
      label: 'Regulatory compliance'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced advisor efficiency'
    }, {
      icon: 'Shield',
      label: 'Automated compliance reporting'
    }]
  },
  'Health Cloud': {
    title: 'Health Cloud',
    description: 'Through healthcare-focused workshops, we design patient-centric solutions that improve care coordination, enhance provider collaboration, and create seamless patient experiences across the care continuum.',
    capabilities: ['Patient 360 view', 'Care plan management', 'Provider collaboration', 'Health data integration'],
    caseStudy: {
      industry: 'Multi-Specialty Clinic Network',
      challenge: 'Patient information siloed across specialties. Care coordination happening via phone and fax. No visibility into patient care journey.',
      solution: 'Implemented Health Cloud with unified patient records, care plan templates, and provider collaboration tools. Integrated with EHR for real-time clinical data.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Improved patient outcomes'
    }, {
      icon: 'Users',
      label: 'Coordinated care delivery'
    }, {
      icon: 'CheckCircle',
      label: 'Enhanced patient engagement'
    }, {
      icon: 'Activity',
      label: 'Real-time care visibility'
    }]
  },
  'Automotive Cloud': {
    title: 'Automotive Cloud',
    description: 'We work with automotive retailers and manufacturers to design connected customer experiences that span vehicle sales, service, and ownership. Our solutions optimize inventory, streamline service operations, and personalize the customer journey.',
    capabilities: ['Vehicle inventory management', 'Service appointment scheduling', 'Customer journey orchestration', 'Dealer performance analytics'],
    caseStudy: {
      industry: 'Auto Dealership Group',
      challenge: 'Disconnected sales and service experiences. Manual service scheduling causing conflicts. No visibility into customer vehicle ownership history.',
      solution: 'Deployed Automotive Cloud connecting sales, service, and parts. Automated service scheduling with technician availability. Created unified customer vehicle profiles.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Enhanced customer experience'
    }, {
      icon: 'BarChart3',
      label: 'Optimized inventory turnover'
    }, {
      icon: 'DollarSign',
      label: 'Increased service revenue'
    }, {
      icon: 'Clock',
      label: 'Streamlined scheduling'
    }]
  },
  'Omni Studio': {
    title: 'Omni Studio',
    description: 'We design industry-specific digital experiences using OmniStudio low-code tools, creating reusable components and guided workflows that accelerate development while maintaining consistency across your applications.',
    capabilities: ['FlexCard and OmniScript development', 'Industry data model configuration', 'Integration procedures', 'Guided user experiences'],
    caseStudy: {
      industry: 'Insurance Provider',
      challenge: 'Custom development taking months for each new product launch. Inconsistent user experiences across lines of business.',
      solution: 'Built reusable OmniStudio components for policy applications, claims processing, and customer service. Reduced development time by 60%.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Rapid application development'
    }, {
      icon: 'CheckCircle',
      label: 'Consistent user experiences'
    }, {
      icon: 'Target',
      label: 'Industry-specific solutions'
    }, {
      icon: 'Layers',
      label: 'Reusable component library'
    }]
  },
  Slack: {
    title: 'Slack Integration',
    description: 'Our workshops identify collaboration bottlenecks and design Slack integrations that bring Salesforce data and workflows into your team conversations, enabling real-time collaboration and faster decision-making.',
    capabilities: ['Salesforce-Slack integration', 'Workflow automation in Slack', 'Real-time notifications', 'Collaborative selling'],
    caseStudy: {
      industry: 'SaaS Company',
      challenge: 'Sales teams switching between Slack and Salesforce constantly. Deal updates happening in silos. No visibility into customer conversations.',
      solution: 'Integrated Salesforce with Slack for deal rooms, automated opportunity updates in channels, and enabled Salesforce actions directly from Slack.'
    },
    outcomes: [{
      icon: 'Users',
      label: 'Faster team collaboration'
    }, {
      icon: 'Zap',
      label: 'Real-time business updates'
    }, {
      icon: 'TrendingUp',
      label: 'Improved productivity'
    }, {
      icon: 'Activity',
      label: 'Centralized deal visibility'
    }]
  },
  APIs: {
    title: 'API Development',
    description: 'We design and build custom APIs that extend Salesforce capabilities and enable seamless integrations with your technology ecosystem. Our approach creates secure, scalable, well-documented APIs that support your business needs.',
    capabilities: ['REST and SOAP API development', 'Custom API endpoints', 'API security and governance', 'Integration architecture'],
    caseStudy: {
      industry: 'E-commerce Platform',
      challenge: 'Third-party systems unable to access Salesforce data. Manual data exports creating delays. No standardized integration approach.',
      solution: 'Built custom REST APIs for order management, customer data, and inventory. Implemented OAuth security and rate limiting. Created developer documentation.'
    },
    outcomes: [{
      icon: 'Layers',
      label: 'Seamless system integration'
    }, {
      icon: 'Zap',
      label: 'Extended platform capabilities'
    }, {
      icon: 'Shield',
      label: 'Secure data exchange'
    }, {
      icon: 'FileCheck',
      label: 'Well-documented APIs'
    }]
  },
  Heroku: {
    title: 'Heroku Development',
    description: 'Through technical workshops, we identify use cases where custom applications on Heroku can extend your Salesforce ecosystem. We build scalable, cloud-native applications that integrate seamlessly with your Salesforce data.',
    capabilities: ['Custom application development', 'Heroku Connect integration', 'Scalable architecture design', 'DevOps and deployment'],
    caseStudy: {
      industry: 'Media Company',
      challenge: 'Customer-facing portal requiring real-time content delivery and complex business logic beyond Salesforce capabilities.',
      solution: 'Built custom Node.js application on Heroku with Heroku Connect for bi-directional Salesforce sync. Scaled to handle 100K+ daily users.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Extended platform capabilities'
    }, {
      icon: 'Gauge',
      label: 'Scalable custom applications'
    }, {
      icon: 'Layers',
      label: 'Seamless Salesforce integration'
    }, {
      icon: 'Activity',
      label: 'High-performance delivery'
    }]
  },
  'Custom Apps': {
    title: 'Custom Application Development',
    description: 'We collaborate with your teams to design and build bespoke Salesforce applications tailored to your unique business requirements. Using Lightning Web Components and Apex, we create solutions that feel native to Salesforce.',
    capabilities: ['Lightning Web Components', 'Apex development', 'Custom UI/UX design', 'Application lifecycle management'],
    caseStudy: {
      industry: 'Logistics Company',
      challenge: 'Complex route optimization and load planning requirements not met by standard Salesforce functionality.',
      solution: 'Built custom application with LWC interface for route planning, Apex algorithms for optimization, and real-time GPS tracking integration.'
    },
    outcomes: [{
      icon: 'Target',
      label: 'Tailored business solutions'
    }, {
      icon: 'TrendingUp',
      label: 'Enhanced user productivity'
    }, {
      icon: 'Sparkles',
      label: 'Competitive differentiation'
    }, {
      icon: 'CheckCircle',
      label: 'Native Salesforce experience'
    }]
  },
  Orchestration: {
    title: 'Process Orchestration',
    description: 'We map complex multi-step processes that span teams and systems, then design orchestration workflows that coordinate work, handle exceptions, and provide visibility into process performance.',
    capabilities: ['Multi-step process design', 'Cross-functional workflow coordination', 'Exception handling', 'Process monitoring and optimization'],
    caseStudy: {
      industry: 'Financial Services Firm',
      challenge: 'Loan approval process involving 5 departments, 12 steps, and multiple systems. Average cycle time of 14 days with frequent errors.',
      solution: 'Designed orchestration workflow coordinating credit checks, underwriting, compliance review, and approval. Automated handoffs and exception routing.'
    },
    outcomes: [{
      icon: 'Workflow',
      label: 'Streamlined operations'
    }, {
      icon: 'Clock',
      label: 'Reduced process cycle times'
    }, {
      icon: 'Users',
      label: 'Improved collaboration'
    }, {
      icon: 'Activity',
      label: 'Real-time process visibility'
    }]
  },
  'Sales Execution': {
    title: 'Sales Execution',
    description: 'We analyze your sales motions and design Sales Execution solutions that automate cadences, track engagement, and provide the insights your team needs to prioritize high-value activities and accelerate pipeline.',
    capabilities: ['Sales cadence automation', 'Engagement tracking', 'Pipeline management', 'Sales analytics'],
    caseStudy: {
      industry: 'B2B Software Company',
      challenge: 'SDRs manually tracking outreach. No visibility into engagement effectiveness. Inconsistent follow-up causing lost opportunities.',
      solution: 'Implemented automated sales cadences with multi-channel touchpoints, engagement scoring, and intelligent task prioritization based on buyer signals.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Increased sales velocity'
    }, {
      icon: 'BarChart3',
      label: 'Better pipeline visibility'
    }, {
      icon: 'Brain',
      label: 'Data-driven sales strategies'
    }, {
      icon: 'Target',
      label: 'Improved conversion rates'
    }]
  },
  Messaging: {
    title: 'Messaging',
    description: 'Our workshops identify opportunities to engage customers through their preferred messaging channels. We design conversational workflows for SMS, WhatsApp, and messaging platforms that feel personal and drive action.',
    capabilities: ['Multi-channel messaging', 'Conversational workflows', 'Message template management', 'Messaging analytics'],
    caseStudy: {
      industry: 'Healthcare Provider',
      challenge: 'Appointment reminders via phone calls causing staff burden. High no-show rates. Patients wanting text-based communication.',
      solution: 'Deployed SMS and WhatsApp messaging for appointment reminders, confirmations, and follow-ups. Built conversational flows for rescheduling.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Enhanced customer engagement'
    }, {
      icon: 'Zap',
      label: 'Real-time communication'
    }, {
      icon: 'CheckCircle',
      label: 'Improved response rates'
    }, {
      icon: 'Clock',
      label: 'Reduced no-show rates'
    }]
  },
  'Live Chat': {
    title: 'Live Chat',
    description: 'We design intelligent live chat solutions that provide instant customer support while optimizing agent efficiency. Our implementations include chatbot integration, smart routing, and seamless escalation to human agents.',
    capabilities: ['Chat widget customization', 'Chat routing and assignment', 'Chatbot integration', 'Chat analytics'],
    caseStudy: {
      industry: 'E-commerce Retailer',
      challenge: 'Customers abandoning carts due to unanswered questions. Support team overwhelmed during peak hours. No 24/7 coverage.',
      solution: 'Implemented live chat with AI chatbot for common questions, intelligent routing to product specialists, and proactive chat triggers based on behavior.'
    },
    outcomes: [{
      icon: 'Zap',
      label: 'Instant customer support'
    }, {
      icon: 'Clock',
      label: 'Reduced wait times'
    }, {
      icon: 'TrendingUp',
      label: 'Increased customer satisfaction'
    }, {
      icon: 'DollarSign',
      label: 'Reduced cart abandonment'
    }]
  },
  'Marketing Automation': {
    title: 'Marketing Automation',
    description: 'Through marketing operations workshops, we design automation workflows that nurture leads, trigger personalized campaigns based on behavior, and drive conversions at scale while maintaining the human touch.',
    capabilities: ['Lead nurturing campaigns', 'Email automation', 'Behavioral triggers', 'Campaign performance tracking'],
    caseStudy: {
      industry: 'B2B Services Company',
      challenge: 'Leads going cold after initial contact. Manual follow-up inconsistent. No visibility into which campaigns drove conversions.',
      solution: 'Built automated nurture campaigns triggered by lead behavior, implemented lead scoring, and created attribution reporting for campaign ROI.'
    },
    outcomes: [{
      icon: 'TrendingUp',
      label: 'Increased lead conversion'
    }, {
      icon: 'Target',
      label: 'Personalized engagement'
    }, {
      icon: 'Gauge',
      label: 'Marketing efficiency'
    }, {
      icon: 'BarChart3',
      label: 'Clear ROI attribution'
    }]
  },
  'Operational AI': {
    title: 'Operational AI',
    description: 'We identify operational processes where AI can automate decisions, detect anomalies, and optimize outcomes. Our implementations embed intelligence into daily workflows for proactive issue resolution and continuous improvement.',
    capabilities: ['Process automation with AI', 'Intelligent recommendations', 'Anomaly detection', 'Predictive analytics'],
    caseStudy: {
      industry: 'Manufacturing Company',
      challenge: 'Quality issues discovered too late in production. Manual inspection processes missing defects. Reactive problem-solving.',
      solution: 'Deployed AI models to predict quality issues based on production parameters, detect anomalies in real-time, and recommend corrective actions.'
    },
    outcomes: [{
      icon: 'Brain',
      label: 'Automated decision-making'
    }, {
      icon: 'Gauge',
      label: 'Operational efficiency'
    }, {
      icon: 'Shield',
      label: 'Proactive issue resolution'
    }, {
      icon: 'TrendingUp',
      label: 'Continuous optimization'
    }]
  },
  'Model Builder': {
    title: 'Model Builder',
    description: 'Our data science workshops identify opportunities for custom AI models tailored to your specific business context. We build, train, and deploy models that deliver predictions and insights unique to your operations.',
    capabilities: ['Custom prediction models', 'Model training and optimization', 'Model deployment and monitoring', 'AI governance'],
    caseStudy: {
      industry: 'Telecommunications Provider',
      challenge: 'Generic churn models not capturing nuances of their customer base. Unable to predict which customers would leave and why.',
      solution: 'Built custom churn prediction model using their specific customer data, behavior patterns, and service usage. Achieved 85% prediction accuracy.'
    },
    outcomes: [{
      icon: 'Target',
      label: 'Tailored AI solutions'
    }, {
      icon: 'TrendingUp',
      label: 'Improved prediction accuracy'
    }, {
      icon: 'Brain',
      label: 'Business-specific intelligence'
    }, {
      icon: 'Shield',
      label: 'Governed AI deployment'
    }]
  }
};
const architectureLayers = [{
  name: 'Foundation',
  services: ['Salesforce Platform', 'Data Cloud', 'Identity'],
  color: 'rgba(79, 12, 154, 0.14)',
  borderColor: 'rgba(79, 12, 154, 0.45)',
  glowColor: '#4f0c9a'
}, {
  name: 'Core Layer',
  services: ['Sales Cloud', 'Service Cloud', 'Marketing Cloud'],
  color: 'rgba(79, 12, 154, 0.12)',
  borderColor: 'rgba(79, 12, 154, 0.4)',
  glowColor: '#4f0c9a'
}, {
  name: 'Platform Layer',
  services: ['Experience Cloud', 'Revenue Cloud', 'Field Service', 'CPQ', 'Financial Services Cloud', 'Health Cloud', 'Automotive Cloud', 'Omni Studio', 'Slack'],
  color: 'rgba(79, 12, 154, 0.1)',
  borderColor: 'rgba(79, 12, 154, 0.35)',
  glowColor: '#4f0c9a'
}, {
  name: 'Integration Layer',
  services: ['MuleSoft', 'APIs', 'Heroku', 'Custom Apps'],
  color: 'rgba(79, 12, 154, 0.08)',
  borderColor: 'rgba(79, 12, 154, 0.3)',
  glowColor: '#4f0c9a'
}, {
  name: 'Automation Layer',
  services: ['Agentforce', 'Flow', 'Omni-Channel', 'Orchestration', 'Sales Execution', 'Messaging', 'Live Chat', 'Marketing Automation', 'Operational AI'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.3)',
  glowColor: '#15c8ff'
}, {
  name: 'Intelligence Layer',
  services: ['Tableau', 'Einstein AI', 'Model Builder'],
  color: 'rgba(250, 255, 49, 0.08)',
  borderColor: 'rgba(250, 255, 49, 0.3)',
  glowColor: '#faff31'
}];
const tabsData = [{
  id: 'salesforce',
  label: 'Salesforce',
  header: 'Salesforce',
  subheading: 'End-to-end Salesforce design, delivery, and evolution.',
  intro: 'Salesforce is one of the platforms we design across. From core clouds to data, integrations, and AI, we deliver it as part of a connected, scalable system rather than in isolation.',
  speechBubble: 'Select a Cloud to Learn More',
  bottomNote: 'End-to-end Salesforce architecture, from foundation to intelligence',
  layers: architectureLayers
}, {
  id: 'erp',
  label: 'ERP',
  header: 'ERP',
  subheading: 'Finance, operations, and the systems that keep your business moving.',
  intro: 'We design, integrate, and modernise ERP environments that connect finance, procurement, inventory, projects, reporting, and operations into one reliable system.',
  speechBubble: 'Select a System to Learn More',
  bottomNote: 'End-to-end ERP architecture, from foundation to intelligence',
  layers: [{
    name: 'Foundation Layer',
    services: ['Finance Core', 'General Ledger', 'Procurement', 'Billing'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Core Operations',
    services: ['Supply Chain', 'Inventory', 'Manufacturing', 'Projects'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Platform Layer',
    services: ['NetSuite', 'SAP', 'Microsoft Dynamics', 'Other ERP platforms'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Integration Layer',
    services: ['APIs', 'Middleware', 'Data Sync', 'Event-driven architecture'],
    color: 'rgba(21, 200, 255, 0.08)',
    borderColor: 'rgba(21, 200, 255, 0.3)',
    glowColor: '#15c8ff'
  }, {
    name: 'Intelligence Layer',
    services: ['Forecasting', 'Financial insights', 'Operational reporting', 'AI-driven planning'],
    color: 'rgba(250, 255, 49, 0.08)',
    borderColor: 'rgba(250, 255, 49, 0.3)',
    glowColor: '#faff31'
  }]
}, {
  id: 'ai',
  label: 'AI & Automation',
  header: 'AI & Automation',
  subheading: 'Intelligence built into how your business operates.',
  intro: 'We embed AI across your workflows, systems, and decision making. The focus is on real outcomes, not experiments.',
  speechBubble: 'Select a Capability to Learn More',
  bottomNote: 'AI architecture, embedded from foundation to intelligence',
  layers: [{
    name: 'Foundation',
    services: ['Data pipelines', 'Data quality', 'Data unification'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Core AI Capabilities',
    services: ['LLM integration', 'Prompt orchestration', 'Vector search', 'Knowledge bases'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Automation Layer',
    services: ['Workflow automation', 'Agent orchestration', 'RPA / task automation'],
    color: 'rgba(21, 200, 255, 0.08)',
    borderColor: 'rgba(21, 200, 255, 0.3)',
    glowColor: '#15c8ff'
  }, {
    name: 'Application Layer',
    services: ['AI copilots', 'Customer agents', 'Internal assistants'],
    color: 'rgba(21, 200, 255, 0.08)',
    borderColor: 'rgba(21, 200, 255, 0.3)',
    glowColor: '#15c8ff'
  }, {
    name: 'Intelligence Layer',
    services: ['Predictions', 'Recommendations', 'Decision support', 'Continuous learning'],
    color: 'rgba(250, 255, 49, 0.08)',
    borderColor: 'rgba(250, 255, 49, 0.3)',
    glowColor: '#faff31'
  }]
}, {
  id: 'custom',
  label: 'Bespoke App Development',
  header: 'Bespoke App Development',
  subheading: 'Built for what your business actually needs.',
  intro: 'We design and build tailored applications, integrations, and digital experiences that fit your architecture and solve real problems.',
  speechBubble: 'Select a Layer to Learn More',
  bottomNote: 'Custom architecture, tailored from foundation to intelligence',
  layers: [{
    name: 'Foundation',
    services: ['Cloud infrastructure', 'Hosting', 'Security'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Application Layer',
    services: ['Web apps', 'Mobile apps', 'Internal tools', 'Customer portals'],
    color: 'rgba(79, 12, 154, 0.12)',
    borderColor: 'rgba(79, 12, 154, 0.4)',
    glowColor: '#4f0c9a'
  }, {
    name: 'Integration Layer',
    services: ['APIs', 'System integrations', 'Data pipelines'],
    color: 'rgba(21, 200, 255, 0.08)',
    borderColor: 'rgba(21, 200, 255, 0.3)',
    glowColor: '#15c8ff'
  }, {
    name: 'Experience Layer',
    services: ['UX/UI design', 'Customer journeys', 'Frontend experiences'],
    color: 'rgba(21, 200, 255, 0.08)',
    borderColor: 'rgba(21, 200, 255, 0.3)',
    glowColor: '#15c8ff'
  }, {
    name: 'Intelligence Layer',
    services: ['Embedded AI', 'Personalisation', 'Automation'],
    color: 'rgba(250, 255, 49, 0.08)',
    borderColor: 'rgba(250, 255, 49, 0.3)',
    glowColor: '#faff31'
  }]
}];
// Group data for non-Salesforce tabs
const erpGroups = [{
  name: 'Finance & Accounting',
  items: ['General Ledger', 'Accounts payable', 'Accounts receivable', 'Billing'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Procurement & Supply Chain',
  items: ['Procurement', 'Supplier management', 'Purchase orders', 'Supply chain visibility'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Inventory & Operations',
  items: ['Inventory management', 'Warehousing', 'Manufacturing', 'Order fulfilment'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'ERP Platforms',
  items: ['NetSuite', 'SAP', 'Microsoft Dynamics', 'Oracle'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Integration & Data',
  items: ['APIs', 'Middleware', 'Data sync', 'Reporting pipelines'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Planning & Intelligence',
  items: ['Forecasting', 'Financial insights', 'Operational reporting', 'AI-driven planning'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}];
const aiGroups = [{
  name: 'Data & Foundations',
  items: ['Data pipelines', 'Data quality', 'Data unification', 'Snowflake / BigQuery'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff',
  step: '01'
}, {
  name: 'AI Capabilities',
  items: ['LLM integration (OpenAI, Anthropic)', 'Prompt orchestration', 'Vector search (Pinecone, Weaviate)', 'Embeddings & retrieval'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff',
  step: '02'
}, {
  name: 'Automation & Agents',
  items: ['Workflow automation (Zapier, n8n)', 'AI agents', 'Process orchestration', 'Task automation'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff',
  step: '03'
}, {
  name: 'Applications & Use Cases',
  items: ['Customer copilots', 'Internal assistants', 'Sales automation', 'Service automation'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff',
  step: '04'
}, {
  name: 'Insights & Optimisation',
  items: ['Predictions', 'Recommendations', 'Decision support', 'Continuous learning'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff',
  step: '05'
}];
const customGroups = [{
  name: 'Applications',
  items: ['Web apps', 'Mobile apps', 'Internal tools', 'Customer platforms'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Customer Experiences',
  items: ['Customer journeys', 'UX/UI design', 'Frontend experiences', 'Digital products'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Backend & Services',
  items: ['APIs', 'Backend services', 'Microservices', 'System logic'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Integration & Connectivity',
  items: ['System integrations', 'Data pipelines', 'Event architecture', 'Third-party integrations'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Platforms & Infrastructure',
  items: ['AWS', 'Azure', 'Hosting', 'Security'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}, {
  name: 'Intelligence & Enhancement',
  items: ['Embedded AI', 'Personalisation', 'Automation', 'Smart workflows'],
  color: 'rgba(21, 200, 255, 0.08)',
  borderColor: 'rgba(21, 200, 255, 0.25)',
  glowColor: '#15c8ff'
}];
export const About: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeTabId, setActiveTabId] = useState('salesforce');
  const activeTab = tabsData.find(t => t.id === activeTabId) || tabsData[0];
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [selectedGlowColor, setSelectedGlowColor] = useState<string>('#4f0c9a');
  const [fallbackModalData, setFallbackModalData] = useState<{
    title: string;
    layerName: string;
    tabHeader: string;
    capabilities: string[];
  } | null>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const titleY = useTransform(scrollYProgress, [0, 0.3], [50, 0]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  // Sunrise glow continues into this section
  const sunriseOpacity = useTransform(scrollYProgress, [0, 0.3, 0.6], [0.3, 0.2, 0]);
  const handleServiceClick = (service: string, glowColor: string, layerName: string) => {
    if (serviceExpertise[service]) {
      setSelectedService(service);
      setFallbackModalData(null);
    } else {
      setSelectedService(service);
      setFallbackModalData({
        title: service,
        layerName: layerName,
        tabHeader: activeTab.header,
        capabilities: activeTab.layers.find(l => l.name === layerName)?.services.filter(s => s !== service) || []
      });
    }
    setSelectedGlowColor(glowColor);
  };
  const closeModal = () => {
    setSelectedService(null);
    setFallbackModalData(null);
  };
  return <section id="about" ref={sectionRef} className="relative py-16 md:py-24 overflow-hidden" data-id="element-16">
      {/* Floating Energy Orbs */}
      <FloatingOrbs count={12} data-id="element-17" />

      {/* Continuing sunrise glow from Hero */}
      <motion.div className="absolute top-0 left-0 right-0 h-[40vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(250, 255, 49, 0.12) 0%, transparent 60%)',
      opacity: sunriseOpacity
    }} data-id="element-18" />

      {/* Bottom glow to connect sections */}
      <motion.div className="absolute bottom-0 left-0 right-0 h-[50vh] pointer-events-none" style={{
      background: 'radial-gradient(ellipse 100% 60% at 50% 100%, rgba(250, 255, 49, 0.18) 0%, transparent 60%)',
      opacity: sunriseOpacity
    }} data-id="element-19" />

      {/* Ambient yellow glow throughout */}
      <div className="absolute inset-0 pointer-events-none opacity-30" data-id="element-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-electric-yellow/10 rounded-full blur-3xl" data-id="element-21" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-electric-yellow/8 rounded-full blur-3xl" data-id="element-22" />
      </div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-23">
        {/* Section-level intro */}
        <motion.div className="text-center max-w-4xl mx-auto mb-12" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} viewport={{
        once: true
      }} style={{
        y: titleY,
        opacity: titleOpacity
      }} data-id="element-24">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-wider mb-4 text-white leading-tight" data-id="element-25">
            Built across the systems powering{' '}
            <span className="text-electric-yellow" data-id="element-26">modern businesses</span>
          </h2>
          <p className="text-base md:text-lg text-white/80 max-w-3xl mx-auto" data-id="element-27">
            We design and connect CRM, ERP, AI, and custom platforms into one
            cohesive system — not disconnected tools.
          </p>
        </motion.div>

        {/* Tab Bar */}
        <motion.div className="mb-12" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true
      }} data-id="element-28">
          {/* Mobile: 2x2 grid */}
          <div className="md:hidden grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-deep-navy/50 backdrop-blur-md border border-white/10" data-id="element-29">
            {tabsData.map(tab => <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className={`relative px-3 py-3 rounded-xl text-xs font-heading font-medium transition-all duration-300 text-center leading-tight min-h-[52px] flex items-center justify-center ${activeTabId === tab.id ? 'text-deep-navy' : 'text-white/70 hover:text-white'}`} data-id="element-30">
                {activeTabId === tab.id && <motion.div layoutId="activeTabMobile" className="absolute inset-0 bg-electric-yellow rounded-xl" style={{
              boxShadow: '0 0 15px rgba(250, 255, 49, 0.4)'
            }} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-31" />}
                <span className="relative z-10" data-id="element-32">{tab.label}</span>
              </button>)}
          </div>

          {/* Desktop: Horizontal pill bar */}
          <div className="hidden md:flex justify-center" data-id="element-33">
            <div className="flex items-center gap-2 p-1.5 rounded-full bg-deep-navy/50 backdrop-blur-md border border-white/10" data-id="element-34">
              {tabsData.map(tab => <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className={`relative px-6 py-2.5 rounded-full text-sm md:text-base font-heading font-medium transition-all duration-300 whitespace-nowrap ${activeTabId === tab.id ? 'text-deep-navy' : 'text-white/70 hover:text-white'}`} data-id="element-35">
                  {activeTabId === tab.id && <motion.div layoutId="activeTab" className="absolute inset-0 bg-electric-yellow rounded-full" style={{
                boxShadow: '0 0 15px rgba(250, 255, 49, 0.4)'
              }} transition={{
                type: 'spring',
                bounce: 0.2,
                duration: 0.6
              }} data-id="element-36" />}
                  <span className="relative z-10" data-id="element-37">{tab.label}</span>
                </button>)}
            </div>
          </div>
        </motion.div>

        {/* Centered Title & Subheading */}
        <AnimatePresence mode="wait" data-id="element-38">
          <motion.div key={activeTabId + '-header'} className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-39">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-wider mb-4 text-white" data-id="element-40">
              {activeTab.header}
            </h2>
            <p className="text-xl md:text-2xl font-heading font-medium text-electric-yellow mb-4" data-id="element-41">
              {activeTab.subheading}
            </p>
            <p className="text-base md:text-lg text-white/80 max-w-3xl mx-auto" data-id="element-42">
              {activeTab.intro}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* CONDITIONAL LAYOUT: Salesforce vs. Others */}
        {activeTabId === 'salesforce' ?
      // SALESFORCE TAB - Keep existing 2-col grid exactly as-is
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 max-w-7xl mx-auto relative" data-id="element-43">
            {/* Left Column - Statistics Tiles (40% = 2 cols) */}
            <motion.div className="grid grid-cols-2 gap-4 lg:grid-cols-1 lg:space-y-0 lg:gap-4 lg:col-span-2" initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-44">
              {/* Stat 1 - Projects */}
              <motion.div className="relative backdrop-blur-sm rounded-lg border p-5 lg:mt-0" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} viewport={{
            once: true
          }} data-id="element-45">
                <div className="flex items-baseline gap-2 mb-2" data-id="element-46">
                  <span className="text-4xl font-heading font-bold text-electric-yellow" data-id="element-47">
                    250+
                  </span>
                  <motion.div className="w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31',
                boxShadow: '0 0 8px #faff31'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-48" />
                </div>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-49">
                  Successfully delivered projects across our team
                </p>
              </motion.div>

              {/* Stat 2 - Experience */}
              <motion.div className="relative backdrop-blur-sm rounded-lg border p-5" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-50">
                <div className="flex items-baseline gap-2 mb-2" data-id="element-51">
                  <span className="text-4xl font-heading font-bold text-electric-yellow" data-id="element-52">
                    30+
                  </span>
                  <span className="text-sm text-white/60" data-id="element-53">years</span>
                  <motion.div className="w-2 h-2 rounded-full" style={{
                backgroundColor: '#15c8ff',
                boxShadow: '0 0 8px #15c8ff'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.5
              }} data-id="element-54" />
                </div>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-55">
                  Collective Salesforce ecosystem experience
                </p>
              </motion.div>

              {/* Stat 3 - Full-Stack Delivery */}
              <motion.div className="relative backdrop-blur-sm rounded-lg border p-5" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-56">
                <div className="flex items-baseline gap-2 mb-2" data-id="element-57">
                  <span className="text-2xl font-heading font-bold text-electric-yellow leading-tight" data-id="element-58">
                    Full-Stack Salesforce Delivery
                  </span>
                  <motion.div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                backgroundColor: '#4f0c9a',
                boxShadow: '0 0 8px #4f0c9a'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1
              }} data-id="element-59" />
                </div>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-60">
                  From platform architecture to AI integration, we deliver
                  complete Salesforce solutions
                </p>
              </motion.div>

              {/* Stat 4 - APAC */}
              <motion.div className="relative backdrop-blur-sm rounded-lg border p-5" style={{
            backgroundColor: 'rgba(21, 200, 255, 0.08)',
            borderColor: 'rgba(21, 200, 255, 0.3)',
            boxShadow: '0 0 20px rgba(21, 200, 255, 0.2), inset 0 0 20px rgba(21, 200, 255, 0.05)'
          }} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} viewport={{
            once: true
          }} data-id="element-61">
                <div className="flex items-baseline gap-2 mb-2" data-id="element-62">
                  <span className="text-4xl font-heading font-bold text-electric-yellow" data-id="element-63">
                    APAC
                  </span>
                  <span className="text-sm text-white/60" data-id="element-64">focused</span>
                  <motion.div className="w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31',
                boxShadow: '0 0 8px #faff31'
              }} animate={{
                scale: [1, 1.3, 1],
                opacity: [0.7, 1, 0.7]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.5
              }} data-id="element-65" />
                </div>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-66">
                  Supporting organisations across Singapore and Australia
                </p>
              </motion.div>
            </motion.div>

            {/* Right Column - Pyramid with Mascot Above (60% = 3 cols) */}
            <motion.div className="relative lg:col-span-3" initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} data-id="element-67">
              {/* Mascot Centered Above Pyramid - Desktop Only */}
              <motion.div className="hidden lg:flex flex-col items-center mb-6" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} viewport={{
            once: true
          }} data-id="element-68">
                {/* Mascot Image */}
                <motion.img src="https://cdn.magicpatterns.com/uploads/o9mAFQYrCvgHdm4iMPAe9j/ChatGPT_Image_Feb_13,_2026,_07_35_13_PM.png" alt="Deploid Mascot" className="w-16 h-16 md:w-20 md:h-20 object-contain" style={{
              filter: 'drop-shadow(0 0 15px rgba(250, 255, 49, 0.5))'
            }} initial={{
              rotate: -5
            }} animate={{
              rotate: [5, -5, 5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-69" />

                {/* Speech Bubble Below Mascot */}
                <motion.div className="relative mt-3" initial={{
              scale: 0.8,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} viewport={{
              once: true
            }} data-id="element-70">
                  <div className="relative px-4 py-2 rounded-lg backdrop-blur-sm border-2" style={{
                backgroundColor: 'rgba(250, 255, 49, 0.15)',
                borderColor: '#faff31',
                boxShadow: '0 0 20px rgba(250, 255, 49, 0.3)'
              }} data-id="element-71">
                    <AnimatePresence mode="wait" data-id="element-72">
                      <motion.p key={activeTabId + '-speech'} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} className="text-sm font-medium whitespace-nowrap" style={{
                    color: '#faff31'
                  }} data-id="element-73">
                        {activeTab.speechBubble}
                      </motion.p>
                    </AnimatePresence>
                    {/* Speech bubble tail pointing down */}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{
                  backgroundColor: 'rgba(250, 255, 49, 0.15)',
                  borderRight: '2px solid #faff31',
                  borderBottom: '2px solid #faff31'
                }} data-id="element-74" />
                  </div>
                </motion.div>

                {/* Sparkles Around Mascot */}
                <motion.div className="absolute inset-0 pointer-events-none" animate={{
              opacity: [0.4, 1, 0.4]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-75">
                  <div className="absolute top-0 -left-4 w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31'
              }} data-id="element-76" />
                  <div className="absolute top-2 right-0 w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#15c8ff'
              }} data-id="element-77" />
                  <div className="absolute bottom-8 -left-2 w-2 h-2 rounded-full" style={{
                backgroundColor: '#faff31'
              }} data-id="element-78" />
                </motion.div>
              </motion.div>

              {/* Desktop: Horizontal Layer Bands */}
              <AnimatePresence mode="wait" data-id="element-79">
                <motion.div key={activeTabId + '-desktop-layers'} className="hidden lg:flex flex-col gap-2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.4
            }} data-id="element-80">
                  {activeTab.layers.slice().reverse().map((layer, index) => {
                // Scale duration with chip count so all rows drift at the same px/s
                const basePxPerSecond = 30 / 3; // 10s per service-set
                const driftDuration = layer.services.length * basePxPerSecond;
                // 4x repetition ensures sparse layers (3 chips) always fill the container
                // and that the -50% loop animation never reveals empty gaps.
                const quadServices = [...layer.services, ...layer.services, ...layer.services, ...layer.services];
                return <motion.div key={layer.name} className="relative w-full rounded-lg border backdrop-blur-sm overflow-hidden flex items-center" style={{
                  backgroundColor: layer.color,
                  borderColor: layer.borderColor,
                  borderWidth: '2px',
                  boxShadow: `0 0 15px ${layer.glowColor}30, inset 0 0 15px ${layer.glowColor}08`,
                  minHeight: '72px'
                }} initial={{
                  opacity: 0,
                  x: 30
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.08
                }} viewport={{
                  once: true
                }} data-id="element-81">
                          <div className="absolute top-0 left-0 right-0 h-[1.5px] pointer-events-none" style={{
                    background: `linear-gradient(to right, ${layer.glowColor}80, ${layer.glowColor}30, transparent)`
                  }} data-id="element-82" />
                          <div className="absolute bottom-0 left-0 right-0 h-[1.5px] pointer-events-none" style={{
                    background: `linear-gradient(to right, transparent, ${layer.glowColor}30, ${layer.glowColor}80)`
                  }} data-id="element-83" />
                          <div className="flex-shrink-0 flex items-center gap-2 px-4 z-10" style={{
                    width: '140px'
                  }} data-id="element-84">
                            <motion.div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                      backgroundColor: layer.glowColor,
                      boxShadow: `0 0 8px ${layer.glowColor}`
                    }} animate={{
                      scale: [1, 1.4, 1],
                      opacity: [0.7, 1, 0.7]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: index * 0.3
                    }} data-id="element-85" />
                            <span className="text-[11px] font-heading font-semibold text-white leading-tight" data-id="element-86">
                              {layer.name}
                            </span>
                          </div>
                          <div className="flex-shrink-0 w-[1px] self-stretch my-3" style={{
                    backgroundColor: `${layer.glowColor}30`
                  }} data-id="element-87" />
                          <div className="flex-1 overflow-hidden relative" style={{
                    height: '72px'
                  }} data-id="element-88">
                            <div className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none" style={{
                      background: `linear-gradient(to right, rgba(28,20,61,0.85), transparent)`
                    }} data-id="element-89" />
                            <div className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none" style={{
                      background: `linear-gradient(to left, rgba(28,20,61,0.85), transparent)`
                    }} data-id="element-90" />
                            {/* 4x repetition: animate -50% = moves 2 sets, always 2 sets filling the view */}
                            <div className="chip-drift absolute flex items-center gap-2 px-4 h-full" style={{
                      animationDuration: `${driftDuration}s`,
                      width: 'max-content'
                    }} data-id="element-91">
                              {quadServices.map((service, serviceIndex) => <button key={serviceIndex} onClick={() => handleServiceClick(service, layer.glowColor, layer.name)} className="flex-shrink-0 rounded border backdrop-blur-sm cursor-pointer transition-all hover:scale-105" style={{
                        backgroundColor: 'rgba(28, 20, 61, 0.55)',
                        borderColor: layer.borderColor.replace('0.45', '0.25').replace('0.4', '0.25').replace('0.35', '0.25').replace('0.3', '0.25'),
                        padding: '0.3rem 0.65rem',
                        boxShadow: `0 0 6px ${layer.glowColor}20`
                      }} onMouseEnter={e => {
                        const row = e.currentTarget.closest('.chip-drift') as HTMLElement | null;
                        if (row) row.style.animationPlayState = 'paused';
                      }} onMouseLeave={e => {
                        const row = e.currentTarget.closest('.chip-drift') as HTMLElement | null;
                        if (row) row.style.animationPlayState = 'running';
                      }} data-id="element-92">
                                  <span className="text-white/90 font-medium whitespace-nowrap leading-tight" style={{
                          fontSize: '10px'
                        }} data-id="element-93">
                                    {service}
                                  </span>
                                </button>)}
                            </div>
                          </div>
                        </motion.div>;
              })}
                </motion.div>
              </AnimatePresence>

              {/* Mobile: Mascot Above + Flat Stacked Bars */}
              <div className="lg:hidden" data-id="element-94">
                {/* Mobile Mascot (duplicate for mobile) */}
                <motion.div className="flex flex-col items-center mb-6" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} viewport={{
              once: true
            }} data-id="element-95">
                  <motion.img src="https://cdn.magicpatterns.com/uploads/o9mAFQYrCvgHdm4iMPAe9j/ChatGPT_Image_Feb_13,_2026,_07_35_13_PM.png" alt="Deploid Mascot" className="w-16 h-16 object-contain" style={{
                filter: 'drop-shadow(0 0 15px rgba(250, 255, 49, 0.5))'
              }} animate={{
                rotate: [5, -5, 5]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-96" />
                  <motion.div className="relative mt-3" initial={{
                scale: 0.8,
                opacity: 0
              }} whileInView={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.5,
                delay: 0.3
              }} viewport={{
                once: true
              }} data-id="element-97">
                    <div className="relative px-4 py-2 rounded-lg backdrop-blur-sm border-2" style={{
                  backgroundColor: 'rgba(250, 255, 49, 0.15)',
                  borderColor: '#faff31',
                  boxShadow: '0 0 20px rgba(250, 255, 49, 0.3)'
                }} data-id="element-98">
                      <AnimatePresence mode="wait" data-id="element-99">
                        <motion.p key={activeTabId + '-mobile-speech'} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} className="text-sm font-medium text-center" style={{
                      color: '#faff31'
                    }} data-id="element-100">
                          {activeTab.speechBubble}
                        </motion.p>
                      </AnimatePresence>
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{
                    backgroundColor: 'rgba(250, 255, 49, 0.15)',
                    borderRight: '2px solid #faff31',
                    borderBottom: '2px solid #faff31'
                  }} data-id="element-101" />
                    </div>
                  </motion.div>
                </motion.div>

                {/* Flat Stacked Bars */}
                <AnimatePresence mode="wait" data-id="element-102">
                  <motion.div key={activeTabId + '-mobile-layers'} className="space-y-3" initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -20
              }} transition={{
                duration: 0.4
              }} data-id="element-103">
                    {activeTab.layers.slice().reverse().map((layer, index) => <motion.div key={index} className="relative backdrop-blur-sm rounded-lg border p-4 cursor-pointer" style={{
                  backgroundColor: layer.color,
                  borderColor: layer.borderColor,
                  boxShadow: `0 0 15px ${layer.glowColor}40, inset 0 0 15px ${layer.glowColor}10`
                }} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.1
                }} viewport={{
                  once: true
                }} whileTap={{
                  scale: 0.98
                }} data-id="element-104">
                          <div className="flex items-center justify-between mb-2" data-id="element-105">
                            <div className="flex items-center gap-2" data-id="element-106">
                              <motion.div className="w-2 h-2 rounded-full" style={{
                        backgroundColor: layer.glowColor,
                        boxShadow: `0 0 8px ${layer.glowColor}`
                      }} animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.7, 1, 0.7]
                      }} transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }} data-id="element-107" />
                              <h3 className="text-sm font-heading font-semibold text-white" data-id="element-108">
                                {layer.name}
                              </h3>
                            </div>
                            <span className="text-xs text-white/60" data-id="element-109">
                              {layer.services.length} services
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5" data-id="element-110">
                            {layer.services.map((service, serviceIndex) => <button key={serviceIndex} onClick={e => {
                      e.stopPropagation();
                      handleServiceClick(service, layer.glowColor, layer.name);
                    }} className="px-2 py-1 rounded border backdrop-blur-sm text-xs text-white/90 font-medium" style={{
                      backgroundColor: 'rgba(28, 20, 61, 0.5)',
                      borderColor: layer.borderColor.replace('0.3', '0.2').replace('0.45', '0.2').replace('0.4', '0.2').replace('0.35', '0.2')
                    }} data-id="element-111">
                                {service}
                              </button>)}
                          </div>
                        </motion.div>)}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div> :
      // NON-SALESFORCE TABS - New full-width grouped layouts
      <AnimatePresence mode="wait" data-id="element-112">
            <motion.div key={activeTabId + '-content'} className="max-w-7xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-113">
              {/* Mascot + Speech Bubble - Centered */}
              <motion.div className="flex flex-col items-center mb-12" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} viewport={{
            once: true
          }} data-id="element-114">
                <motion.img src="https://cdn.magicpatterns.com/uploads/o9mAFQYrCvgHdm4iMPAe9j/ChatGPT_Image_Feb_13,_2026,_07_35_13_PM.png" alt="Deploid Mascot" className="w-16 h-16 md:w-20 md:h-20 object-contain" style={{
              filter: 'drop-shadow(0 0 15px rgba(250, 255, 49, 0.5))'
            }} animate={{
              rotate: [5, -5, 5]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-115" />
                <motion.div className="relative mt-3" initial={{
              scale: 0.8,
              opacity: 0
            }} whileInView={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} viewport={{
              once: true
            }} data-id="element-116">
                  <div className="relative px-4 py-2 rounded-lg backdrop-blur-sm border-2" style={{
                backgroundColor: 'rgba(250, 255, 49, 0.15)',
                borderColor: '#faff31',
                boxShadow: '0 0 20px rgba(250, 255, 49, 0.3)'
              }} data-id="element-117">
                    <p className="text-sm font-medium whitespace-nowrap" style={{
                  color: '#faff31'
                }} data-id="element-118">
                      {activeTab.speechBubble}
                    </p>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45" style={{
                  backgroundColor: 'rgba(250, 255, 49, 0.15)',
                  borderRight: '2px solid #faff31',
                  borderBottom: '2px solid #faff31'
                }} data-id="element-119" />
                  </div>
                </motion.div>
              </motion.div>

              {/* ERP TAB - Business Operations Grid */}
              {activeTabId === 'erp' && <div data-id="element-120">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white text-center mb-10" data-id="element-121">
                    How your ERP environment works as one system
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr" data-id="element-122">
                    {erpGroups.map((group, index) => <motion.div key={group.name} className="relative backdrop-blur-sm rounded-xl border p-6 flex flex-col" style={{
                backgroundColor: group.color,
                borderColor: group.borderColor,
                boxShadow: `0 0 24px ${group.glowColor}10, inset 0 0 0 1px rgba(255,255,255,0.02)`
              }} initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: index * 0.1
              }} viewport={{
                once: true
              }} whileHover={{
                y: -4
              }} data-id="element-123">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{
                  borderColor: group.borderColor
                }} data-id="element-124">
                          <motion.div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: group.glowColor,
                    boxShadow: `0 0 8px ${group.glowColor}`
                  }} animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.7, 1, 0.7]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-125" />
                          <h4 className="text-lg font-heading font-bold text-white" data-id="element-126">
                            {group.name}
                          </h4>
                        </div>
                        <div className="space-y-2" data-id="element-127">
                          {group.items.map((item, itemIndex) => <motion.button key={itemIndex} onClick={() => handleServiceClick(item, group.glowColor, group.name)} className="w-full text-left px-3 py-2 rounded-lg border backdrop-blur-sm transition-all" style={{
                    backgroundColor: 'rgba(28, 20, 61, 0.4)',
                    borderColor: group.borderColor.replace('0.4', '0.2').replace('0.3', '0.2')
                  }} whileHover={{
                    scale: 1.02,
                    backgroundColor: 'rgba(28, 20, 61, 0.6)',
                    boxShadow: `0 0 12px ${group.glowColor}40`
                  }} initial={{
                    opacity: 0,
                    x: -10
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.3 + itemIndex * 0.05
                  }} viewport={{
                    once: true
                  }} data-id="element-128">
                              <span className="text-sm text-white/90 font-medium" data-id="element-129">
                                {item}
                              </span>
                            </motion.button>)}
                        </div>
                      </motion.div>)}
                  </div>
                </div>}

              {/* AI TAB - Capability Flow */}
              {activeTabId === 'ai' && <div data-id="element-130">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white text-center mb-10" data-id="element-131">
                    How intelligence is applied across your business
                  </h3>
                  {/* Desktop: Horizontal Flow */}
                  <div className="hidden lg:flex items-stretch gap-4 relative" data-id="element-132">
                    {aiGroups.map((group, index) => <Fragment key={group.name} data-id="element-133">
                        <motion.div className="flex-1 relative backdrop-blur-sm rounded-xl border p-5 flex flex-col" style={{
                  backgroundColor: group.color,
                  borderColor: group.borderColor,
                  boxShadow: `0 0 24px ${group.glowColor}10, inset 0 0 0 1px rgba(255,255,255,0.02)`
                }} initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.15
                }} viewport={{
                  once: true
                }} whileHover={{
                  y: -4
                }} data-id="element-134">
                          <div className="flex items-center gap-2 mb-3" data-id="element-135">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-heading font-bold" style={{
                      backgroundColor: group.glowColor,
                      color: '#1c143d',
                      boxShadow: `0 0 12px ${group.glowColor}`
                    }} data-id="element-136">
                              {group.step}
                            </div>
                            <h4 className="text-base font-heading font-bold text-white leading-tight" data-id="element-137">
                              {group.name}
                            </h4>
                          </div>
                          <div className="space-y-2" data-id="element-138">
                            {group.items.map((item, itemIndex) => <motion.button key={itemIndex} onClick={() => handleServiceClick(item, group.glowColor, group.name)} className="w-full text-left px-3 py-2 rounded-lg border backdrop-blur-sm transition-all" style={{
                      backgroundColor: 'rgba(28, 20, 61, 0.4)',
                      borderColor: group.borderColor.replace('0.4', '0.2').replace('0.3', '0.2')
                    }} whileHover={{
                      scale: 1.02,
                      backgroundColor: 'rgba(28, 20, 61, 0.6)',
                      boxShadow: `0 0 12px ${group.glowColor}40`
                    }} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.4,
                      delay: 0.4 + itemIndex * 0.05
                    }} viewport={{
                      once: true
                    }} data-id="element-139">
                                <span className="text-xs text-white/90 font-medium leading-tight" data-id="element-140">
                                  {item}
                                </span>
                              </motion.button>)}
                          </div>
                        </motion.div>
                        {index < aiGroups.length - 1 && <motion.div className="flex items-center justify-center pt-20" initial={{
                  opacity: 0,
                  scale: 0.8
                }} whileInView={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.15 + 0.3
                }} viewport={{
                  once: true
                }} data-id="element-141">
                            <svg className="w-6 h-6 text-electric-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-142">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-143" />
                            </svg>
                          </motion.div>}
                      </Fragment>)}
                  </div>
                  {/* Mobile: Vertical Stack */}
                  <div className="lg:hidden space-y-4" data-id="element-144">
                    {aiGroups.map((group, index) => <Fragment key={group.name} data-id="element-145">
                        <motion.div className="relative backdrop-blur-sm rounded-xl border p-5" style={{
                  backgroundColor: group.color,
                  borderColor: group.borderColor,
                  boxShadow: `0 0 24px ${group.glowColor}10, inset 0 0 0 1px rgba(255,255,255,0.02)`
                }} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.1
                }} viewport={{
                  once: true
                }} data-id="element-146">
                          <div className="flex items-center gap-2 mb-3" data-id="element-147">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-heading font-bold" style={{
                      backgroundColor: group.glowColor,
                      color: '#1c143d',
                      boxShadow: `0 0 12px ${group.glowColor}`
                    }} data-id="element-148">
                              {group.step}
                            </div>
                            <h4 className="text-base font-heading font-bold text-white" data-id="element-149">
                              {group.name}
                            </h4>
                          </div>
                          <div className="space-y-2" data-id="element-150">
                            {group.items.map((item, itemIndex) => <button key={itemIndex} onClick={() => handleServiceClick(item, group.glowColor, group.name)} className="w-full text-left px-3 py-2 rounded-lg border backdrop-blur-sm" style={{
                      backgroundColor: 'rgba(28, 20, 61, 0.4)',
                      borderColor: group.borderColor.replace('0.4', '0.2').replace('0.3', '0.2')
                    }} data-id="element-151">
                                <span className="text-sm text-white/90 font-medium" data-id="element-152">
                                  {item}
                                </span>
                              </button>)}
                          </div>
                        </motion.div>
                        {index < aiGroups.length - 1 && <div className="flex justify-center" data-id="element-153">
                            <svg className="w-6 h-6 text-electric-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-154">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-155" />
                            </svg>
                          </div>}
                      </Fragment>)}
                  </div>
                </div>}

              {/* CUSTOM TAB - Uniform 3x2 Grid */}
              {activeTabId === 'custom' && <div data-id="element-156">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white text-center mb-10" data-id="element-157">
                    Built for your exact needs
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr" data-id="element-158">
                    {customGroups.map((group, index) => <motion.div key={group.name} className="relative backdrop-blur-sm rounded-xl border p-6 flex flex-col" style={{
                backgroundColor: group.color,
                borderColor: group.borderColor,
                boxShadow: `0 0 24px ${group.glowColor}10, inset 0 0 0 1px rgba(255,255,255,0.02)`
              }} initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: index * 0.1
              }} viewport={{
                once: true
              }} whileHover={{
                y: -4
              }} data-id="element-159">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{
                  borderColor: group.borderColor
                }} data-id="element-160">
                          <motion.div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: group.glowColor,
                    boxShadow: `0 0 8px ${group.glowColor}`
                  }} animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.7, 1, 0.7]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: index * 0.3
                  }} data-id="element-161" />
                          <h4 className="text-lg font-heading font-bold text-white" data-id="element-162">
                            {group.name}
                          </h4>
                        </div>
                        <div className="space-y-2 flex-1" data-id="element-163">
                          {group.items.map((item, itemIndex) => <motion.button key={itemIndex} onClick={() => handleServiceClick(item, group.glowColor, group.name)} className="w-full text-left px-3 py-2 rounded-lg border backdrop-blur-sm transition-all" style={{
                    backgroundColor: 'rgba(28, 20, 61, 0.4)',
                    borderColor: group.borderColor.replace('0.4', '0.2').replace('0.3', '0.2')
                  }} whileHover={{
                    scale: 1.02,
                    backgroundColor: 'rgba(28, 20, 61, 0.6)',
                    boxShadow: `0 0 12px ${group.glowColor}40`
                  }} initial={{
                    opacity: 0,
                    x: -10
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    duration: 0.4,
                    delay: 0.3 + itemIndex * 0.05
                  }} viewport={{
                    once: true
                  }} data-id="element-164">
                              <span className="text-sm text-white/90 font-medium" data-id="element-165">
                                {item}
                              </span>
                            </motion.button>)}
                        </div>
                      </motion.div>)}
                  </div>
                </div>}
            </motion.div>
          </AnimatePresence>}

        {/* Bottom note */}
        <AnimatePresence mode="wait" data-id="element-166">
          <motion.div key={activeTabId + '-bottom'} className="mt-12 text-center" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.4
        }} data-id="element-167">
            <p className="text-white/60 text-sm" data-id="element-168">{activeTab.bottomNote}</p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Expertise Modal */}
      <AnimatePresence data-id="element-169">
        {selectedService && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={closeModal} data-id="element-170" />

            {/* Modal */}
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-171">
              <motion.div className="relative w-full max-w-3xl backdrop-blur-xl rounded-2xl border-2 p-6 md:p-8 pointer-events-auto max-h-[85vh] overflow-y-auto" style={{
            backgroundColor: 'rgba(28, 20, 61, 0.95)',
            borderColor: selectedGlowColor,
            boxShadow: `0 0 40px ${selectedGlowColor}60, inset 0 0 40px ${selectedGlowColor}10`
          }} initial={{
            scale: 0.9,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.9,
            y: 20
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300
          }} data-id="element-172">
                {/* Close button */}
                <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full border transition-all hover:scale-110" style={{
              borderColor: selectedGlowColor,
              backgroundColor: 'rgba(28, 20, 61, 0.8)'
            }} data-id="element-173">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{
                color: selectedGlowColor
              }} data-id="element-174">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-175" />
                  </svg>
                </button>

                {fallbackModalData ? <>
                    {/* Fallback Modal Content */}
                    <div className="flex items-center gap-3 mb-6" data-id="element-176">
                      <motion.div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: selectedGlowColor,
                  boxShadow: `0 0 15px ${selectedGlowColor}`
                }} animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.8, 1, 0.8]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-177" />
                      <h2 className="text-3xl font-heading font-bold" style={{
                  color: selectedGlowColor
                }} data-id="element-178">
                        {fallbackModalData.title}
                      </h2>
                    </div>

                    <div className="mb-8 pl-4 border-l-2" style={{
                borderColor: selectedGlowColor
              }} data-id="element-179">
                      <p className="text-white/90 text-lg leading-relaxed" data-id="element-180">
                        Part of our {fallbackModalData.layerName} capabilities
                        within {fallbackModalData.tabHeader}. We design this as
                        part of an integrated system, ensuring it works
                        cohesively with your broader architecture.
                      </p>
                    </div>

                    {fallbackModalData.capabilities.length > 0 && <div className="mb-8" data-id="element-181">
                        <h3 className="text-white font-heading font-semibold text-xl mb-4" data-id="element-182">
                          Related Capabilities in {fallbackModalData.layerName}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-183">
                          {fallbackModalData.capabilities.map((capability, index) => <motion.div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10" initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: index * 0.05
                  }} data-id="element-184">
                                <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{
                      backgroundColor: selectedGlowColor,
                      boxShadow: `0 0 8px ${selectedGlowColor}`
                    }} data-id="element-185" />
                                <span className="text-white/80 text-sm leading-relaxed" data-id="element-186">
                                  {capability}
                                </span>
                              </motion.div>)}
                        </div>
                      </div>}
                  </> : <>
                    {/* Existing serviceExpertise Modal Content */}
                    <div className="flex items-center gap-3 mb-6" data-id="element-187">
                      <motion.div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: selectedGlowColor,
                  boxShadow: `0 0 15px ${selectedGlowColor}`
                }} animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.8, 1, 0.8]
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-188" />
                      <h2 className="text-3xl font-heading font-bold" style={{
                  color: selectedGlowColor
                }} data-id="element-189">
                        {serviceExpertise[selectedService].title}
                      </h2>
                    </div>

                    {/* Workshop Intro */}
                    <div className="mb-8 pl-4 border-l-2" style={{
                borderColor: selectedGlowColor
              }} data-id="element-190">
                      <p className="text-white/90 text-lg leading-relaxed" data-id="element-191">
                        {serviceExpertise[selectedService].description}
                      </p>
                    </div>

                    {/* Capabilities Grid */}
                    <div className="mb-8" data-id="element-192">
                      <h3 className="text-white font-heading font-semibold text-xl mb-4" data-id="element-193">
                        Our Capabilities
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-194">
                        {serviceExpertise[selectedService].capabilities.map((capability, index) => <motion.div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10" initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: index * 0.05
                  }} data-id="element-195">
                              <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{
                      backgroundColor: selectedGlowColor,
                      boxShadow: `0 0 8px ${selectedGlowColor}`
                    }} data-id="element-196" />
                              <span className="text-white/80 text-sm leading-relaxed" data-id="element-197">
                                {capability}
                              </span>
                            </motion.div>)}
                      </div>
                    </div>

                    {/* Outcomes */}
                    <div data-id="element-198">
                      <h3 className="text-white font-heading font-semibold text-xl mb-4" data-id="element-199">
                        Outcomes
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-200">
                        {serviceExpertise[selectedService].outcomes.map((outcome, index) => {
                    const IconComponent = iconMap[outcome.icon] || CheckCircle;
                    return <motion.div key={index} className="flex items-center gap-3 p-3.5 rounded-lg border backdrop-blur-sm" style={{
                      backgroundColor: `${selectedGlowColor}10`,
                      borderColor: `${selectedGlowColor}30`
                    }} initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.3 + index * 0.05
                    }} data-id="element-201">
                                <IconComponent className="w-5 h-5 flex-shrink-0" style={{
                        color: selectedGlowColor
                      }} data-id="element-202" />
                                <span className="text-white/90 text-sm font-medium leading-tight" data-id="element-203">
                                  {outcome.label}
                                </span>
                              </motion.div>;
                  })}
                      </div>
                    </div>
                  </>}

                {/* Holographic edge effect */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
              boxShadow: `inset 0 0 60px ${selectedGlowColor}20`
            }} data-id="element-204" />
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
};